import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import i18n from '@/i18n';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import {
  pickVideoPosterSignOptions,
  pickVideoSpaceSignOptions,
  resolveSafeVideoPlaySrc,
  resolveVideoSpaceCreatorAccountLabel,
  resolveVideoSpacePosterUrl,
} from '@/pages/ipe/workbench/utils/videoSpaceDisplayAccess';
import { formatStorageBytesLabel } from '@/pages/ipe/workbench/utils/imageSpaceStorageStats';
import { resolveVideoByteSize } from '@/pages/ipe/workbench/utils/videoSpaceStorageStats';
import {
  downloadVideoSpaceVideo,
  resolveVideoSpacePlayUrl,
} from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import {
  beginNestedMediaPreview,
  endNestedMediaPreview,
} from '@/pages/ipe/workbench/utils/nestedMediaPreviewGuard';

function metaRow(label, value) {
  return (
    <div className="funding-image-space-preview__meta-row">
      <span className="funding-image-space-preview__meta-label">{label}</span>
      <span className="funding-image-space-preview__meta-value">{value}</span>
    </div>
  );
}

function resolvePosterUrl(video) {
  return resolveVideoSpacePosterUrl(video);
}

function inferVideoFormatLabel(video) {
  const stored = String(video?.format || '').trim().replace(/^\./, '');
  if (stored && stored !== '—') return stored.replace(/^jpeg$/i, 'jpg');

  const fromName = (video?.name || video?.fileName || '').match(/\.([a-zA-Z0-9]+)$/);
  if (fromName) return fromName[1].toLowerCase();

  const playUrl = resolveVideoSpacePlayUrl(video);
  if (playUrl) {
    const pathOnly = playUrl.split('?')[0].replace(/\\/g, '/');
    const fromPath = pathOnly.match(/\.([a-zA-Z0-9]+)$/);
    if (fromPath) return fromPath[1].toLowerCase();
  }
  return '—';
}

function stripVideoDisplayName(name) {
  return String(name || '')
    .trim()
    .replace(/\.(mp4|webm|mov|m4v|mkv|avi|mpeg|mpg)$/i, '')
    || String(name || '').trim()
    || i18n.t('workbench:videoPreview.unnamed');
}

function formatSourceLabel(source) {
  const key = String(source || '').trim();
  const normalized = key.toLowerCase();
  if (normalized === 'texttovideo') return i18n.t('workbench:imageSource.textToVideo');
  if (normalized === 'videos') return i18n.t('workbench:imageSource.videos');
  if (normalized === 'upload') return i18n.t('workbench:imageSource.upload');
  return key || '—';
}

function formatVideoCreatedAt(raw) {
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'string' && raw.includes('T') && (raw.includes('Z') || raw.includes('+'))) {
    try {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString(i18n.language || 'zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      }
    } catch {
      /* ignore */
    }
  }
  return String(raw);
}

export default function VideoSpacePreviewModal({
  open,
  video: initialVideo,
  navList = [],
  onClose,
  showTrashAction = false,
  onMoveToTrash,
  onDownload,
  onDownloadError,
  authUserId = '',
  authUser = null,
  teamMembers = [],
}) {
  const navListRef = useRef(navList);
  navListRef.current = navList;

  const [previewVideo, setPreviewVideo] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open || !initialVideo) {
      setPreviewVideo(null);
      return undefined;
    }
    setPreviewVideo(initialVideo);
    let cancelled = false;
    const loadDetail = async () => {
      if (!initialVideo?.id) return;
      setLoadingDetail(true);
      try {
        const { videoSpaceAPI } = await import('@/utils/api');
        const res = await videoSpaceAPI.getById(String(initialVideo.id));
        const remote = res?.video ?? res?.data ?? res;
        if (cancelled || !remote || typeof remote !== 'object') return;
        setPreviewVideo((prev) => (prev && String(prev.id) === String(initialVideo.id)
          ? {
            ...prev,
            ...remote,
            name: (prev.name && String(prev.name).trim()) || remote.name || remote.fileName || prev.name,
            fileName: prev.fileName || remote.fileName || remote.name,
            createdAt: prev.createdAt || remote.createdAt || remote.uploadedAt,
            outputVideoUrl: remote.outputVideoUrl || prev.outputVideoUrl,
            outputVideoUrlDisplay: remote.outputVideoUrlDisplay || prev.outputVideoUrlDisplay,
            videoUrlDisplay: remote.videoUrlDisplay || prev.videoUrlDisplay,
            outputVideoPosterUrlDisplay: remote.outputVideoPosterUrlDisplay || prev.outputVideoPosterUrlDisplay,
            previewDisplayUrl: remote.previewDisplayUrl || prev.previewDisplayUrl,
            ownerName: remote.ownerName || prev.ownerName,
            ownerId: remote.ownerId ?? prev.ownerId,
            ownerUserId: remote.ownerUserId ?? prev.ownerUserId,
            createdBy: remote.createdBy || prev.createdBy,
            createdByName: remote.createdByName || prev.createdByName,
            url: remote.url || prev.url,
            videoUrl: remote.videoUrl || prev.videoUrl,
          }
          : prev));
      } catch (error) {
        console.warn('加载视频详情失败:', error);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };
    void loadDetail();
    return () => { cancelled = true; };
  }, [open, initialVideo]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const loadPreview = useCallback((video) => {
    if (!video) return;
    setPreviewVideo(video);
  }, []);

  const stepPreview = useCallback(async (delta) => {
    const list = navListRef.current;
    if (!Array.isArray(list) || list.length === 0 || !previewVideo) return;
    const idx = list.findIndex((item) => String(item.id) === String(previewVideo.id));
    if (idx < 0) return;
    const target = list[idx + delta];
    if (!target) return;
    loadPreview(target);
    if (!target?.id) return;
    setLoadingDetail(true);
    try {
      const { videoSpaceAPI } = await import('@/utils/api');
      const res = await videoSpaceAPI.getById(String(target.id));
      const remote = res?.video ?? res?.data ?? res;
      if (remote && typeof remote === 'object') {
        setPreviewVideo((prev) => (prev && String(prev.id) === String(target.id)
          ? { ...prev, ...remote, name: prev.name || remote.name || remote.fileName }
          : prev));
      }
    } catch (error) {
      console.warn('加载视频详情失败:', error);
    } finally {
      setLoadingDetail(false);
    }
  }, [loadPreview, previewVideo]);

  useEffect(() => {
    if (!open || !previewVideo) return undefined;
    beginNestedMediaPreview();
    return () => {
      endNestedMediaPreview();
    };
  }, [open, previewVideo]);

  useEffect(() => {
    if (!open || !previewVideo) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepPreview(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepPreview(1);
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, previewVideo, stepPreview, handleClose]);

  const playRaw = previewVideo ? resolveVideoSpacePlayUrl(previewVideo) : '';
  const posterRaw = previewVideo ? resolvePosterUrl(previewVideo) : '';
  const playSignOptions = useMemo(
    () => pickVideoSpaceSignOptions(previewVideo, playRaw),
    [previewVideo, playRaw],
  );
  const posterSignOptions = useMemo(
    () => pickVideoPosterSignOptions(previewVideo, posterRaw),
    [previewVideo, posterRaw],
  );
  const signedPlayUrl = useSignedImageSrc(playRaw, playSignOptions);
  const signedPosterUrl = useSignedImageSrc(posterRaw, { ...posterSignOptions, variant: 'thumb' });

  const navIndex = useMemo(
    () => navList.findIndex((item) => String(item.id) === String(previewVideo?.id)),
    [navList, previewVideo?.id],
  );
  const canPreviewPrev = navIndex > 0;
  const canPreviewNext = navIndex >= 0 && navIndex < navList.length - 1;

  if (!open || !previewVideo) return null;

  const displayName = stripVideoDisplayName(previewVideo.name || previewVideo.fileName);
  const formatLabel = inferVideoFormatLabel(previewVideo);
  const bytes = resolveVideoByteSize(previewVideo);
  const fileSizeLabel = bytes > 0 ? formatStorageBytesLabel(bytes, { digits: 2 }) : '—';
  const usage = Number(previewVideo.usageCount ?? previewVideo.useCount ?? 0) || 0;
  const durationSec = Number(previewVideo.duration ?? previewVideo.durationSec ?? 0);
  const durationLabel = durationSec > 0
    ? `${Math.floor(durationSec / 60)}:${String(Math.floor(durationSec % 60)).padStart(2, '0')}`
    : '—';

  const handleDownload = () => {
    if (!previewVideo) return;
    if (typeof onDownload === 'function') {
      onDownload(previewVideo);
      return;
    }
    downloadVideoSpaceVideo(previewVideo, { onError: onDownloadError });
  };

  const handleMoveToTrash = async () => {
    if (!previewVideo?.id || !onMoveToTrash) return;
    const currentId = previewVideo.id;
    const list = navListRef.current;
    const navIdx = list.findIndex((item) => String(item.id) === String(currentId));
    const nextTarget = list[navIdx + 1] || list[navIdx - 1];
    navListRef.current = list.filter((item) => String(item.id) !== String(currentId));
    await onMoveToTrash(previewVideo);
    if (nextTarget) {
      loadPreview(nextTarget);
    } else {
      handleClose();
    }
  };

  const videoReady = Boolean(playRaw);
  const videoSrc = resolveSafeVideoPlaySrc(signedPlayUrl, playRaw);
  const posterSrc = resolveSafeVideoPlaySrc(signedPosterUrl, posterRaw);

  return createPortal(
    <div
      className="funding-image-space-preview-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={i18n.t('workbench:videoPreview.title')}
      data-ipe-nested-media-preview="1"
    >
      <div
        className="funding-image-space-preview-stack"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="funding-image-space-preview-shell">
          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={i18n.t('workbench:videoPreview.prev')}
            disabled={!canPreviewPrev || loadingDetail}
            onClick={() => stepPreview(-1)}
          >
            <ChevronLeft size={22} strokeWidth={2.25} aria-hidden />
          </button>

          <div className="funding-image-space-preview-dialog">
            <button
              type="button"
              className="funding-image-space-preview-close"
              aria-label={i18n.t('workbench:videoPreview.close')}
              onClick={handleClose}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>
            <div className="funding-image-space-preview__viewport">
              {loadingDetail && !videoReady ? (
                <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:videoPreview.loading')}</div>
              ) : videoReady ? (
                <div className="funding-image-space-preview__stage">
                  {playRaw && !videoSrc ? (
                    <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:videoPreview.loading')}</div>
                  ) : null}
                  {videoSrc ? (
                    <video
                      key={`${previewVideo.id}-${videoSrc}`}
                      className="funding-image-space-preview__video"
                      src={videoSrc}
                      poster={posterSrc || undefined}
                      controls
                      playsInline
                      autoPlay
                      onClick={(event) => event.stopPropagation()}
                    />
                  ) : null}
                </div>
              ) : (
                <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:videoPreview.loadingOrMissing')}</div>
              )}
            </div>

            <aside className="funding-image-space-preview__aside">
              <div className="funding-image-space-preview__title">
                {displayName}
              </div>
              <div className="funding-image-space-preview__section-title">
                {i18n.t('workbench:videoPreview.basicInfo')}
              </div>
              {metaRow(i18n.t('workbench:videoPreview.format'), formatLabel)}
              {metaRow(i18n.t('workbench:videoPreview.fileSize'), fileSizeLabel)}
              {metaRow(i18n.t('workbench:videoPreview.duration'), durationLabel)}
              {metaRow(
                i18n.t('workbench:videoPreview.createdAt'),
                formatVideoCreatedAt(previewVideo.createdAt || previewVideo.uploadedAt),
              )}
              {metaRow(
                i18n.t('workbench:videoPreview.creator'),
                resolveVideoSpaceCreatorAccountLabel(previewVideo, {
                  authUserId,
                  user: authUser,
                  teamMembers,
                }),
              )}
              {metaRow(i18n.t('workbench:videoPreview.source'), formatSourceLabel(previewVideo.source))}
              {metaRow(
                i18n.t('workbench:videoPreview.taskId'),
                previewVideo.taskId || previewVideo.task_id || '—',
              )}
              <div className="funding-image-space-preview__section-title funding-image-space-preview__section-title--spaced">
                {i18n.t('workbench:videoPreview.others')}
              </div>
              {metaRow(i18n.t('workbench:videoPreview.usageCount'), String(usage))}
              {navList.length > 1 && navIndex >= 0
                ? metaRow(
                  i18n.t('workbench:videoPreview.position'),
                  `${navIndex + 1} / ${navList.length}`,
                )
                : null}
            </aside>
          </div>

          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={i18n.t('workbench:videoPreview.next')}
            disabled={!canPreviewNext || loadingDetail}
            onClick={() => stepPreview(1)}
          >
            <ChevronRight size={22} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <div className="funding-image-space-preview-footer">
          {showTrashAction ? (
            <button
              type="button"
              className="funding-image-space-preview-action funding-image-space-preview-action--trash"
              onClick={handleMoveToTrash}
            >
              {i18n.t('workbench:videoPreview.moveToTrash')}
            </button>
          ) : (
            <span className="funding-image-space-preview-footer__spacer" aria-hidden />
          )}
          <button
            type="button"
            className="funding-image-space-preview-action funding-image-space-preview-action--download"
            onClick={handleDownload}
            disabled={!previewVideo}
          >
            <Download size={16} strokeWidth={2} aria-hidden />
            {i18n.t('workbench:videoPreview.download')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
